// Formulário de criar/editar deal CRM (Story 11.4).
// - RHF + zodResolver (createCrmDealSchema do @control-center/shared — fonte única)
// - Picker de responsável (useUsers), produto SaaS, plano — todos opcionais
// - lead/cliente vêm pré-preenchidos (deal a partir de um lead/cliente) ou vazios
// - Validação onBlur (erros) + onChange (limpa) — padrão LeadForm/ClientForm
// - Submit desabilitado enquanto pending (AC10)
//
// NÃO edita `stage` (stage só via ações won/lost / kanban 11.5).

import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import type { z } from 'zod';

import { createCrmDealSchema } from '@control-center/shared';

import { firstErrorMessage } from '../../lib/form-errors';
import { Field, Input, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

const ORIGIN_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'self_service', label: 'Self-service' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'referral', label: 'Indicação' },
  { value: 'upsell', label: 'Upsell' },
];

export interface DealOption {
  id: string;
  name: string;
}

export type DealFormValues = z.input<typeof createCrmDealSchema>;
export type DealFormSubmit = z.output<typeof createCrmDealSchema>;

interface DealFormProps {
  initialValues?: Partial<DealFormValues>;
  isPending?: boolean;
  submitLabel?: string;
  responsibleUsers?: DealOption[];
  saasProducts?: DealOption[];
  plans?: DealOption[];
  /** Nome do lead/cliente vinculado (read-only) quando o deal nasce de um deles. */
  linkedLeadName?: string | null;
  linkedClientName?: string | null;
  onSubmit: (values: DealFormSubmit) => void | Promise<void>;
  onCancel?: () => void;
}

const DEFAULT_VALUES: DealFormValues = {
  title: '',
  lead_id: null,
  client_id: null,
  saas_product_id: null,
  plan_id: null,
  value: null,
  stage: 'novo_lead',
  expected_close_date: null,
  responsible_user_id: null,
  origin: null,
};

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-3 text-[13px] font-semibold tracking-[-0.005em] text-ink">{children}</h2>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">{children}</section>
  );
}

function emptyToNull(v: string): string | null {
  return v === '' ? null : v;
}

/** Valor monetário: string vazia → null; senão número (positivo validado no Zod). */
function valueToNumber(v: string): number | null {
  if (v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function DealForm({
  initialValues,
  isPending,
  submitLabel = 'Salvar',
  responsibleUsers = [],
  saasProducts = [],
  plans = [],
  linkedLeadName,
  linkedClientName,
  onSubmit,
  onCancel,
}: DealFormProps): JSX.Element {
  const merged: DealFormValues = { ...DEFAULT_VALUES, ...initialValues };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<DealFormValues>({
    resolver: zodResolver(createCrmDealSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: merged,
  });

  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const { ref: titleRefRHF, ...titleRest } = register('title');

  const handleValid = (values: DealFormValues): void | Promise<void> => {
    const parsed = createCrmDealSchema.parse(values);
    return onSubmit(parsed);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValid, (errs) =>
        toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
      )}
      className="flex flex-col gap-4"
      noValidate
    >
      {/* Identificação */}
      <Card>
        <SectionTitle>Negociação</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Título" required error={errors.title?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Ex: OwlTask para clínica X"
                hasError={!!errors.title}
                ref={(el) => {
                  titleRefRHF(el);
                  firstFieldRef.current = el;
                }}
                {...titleRest}
              />
            )}
          </Field>

          {(linkedLeadName || linkedClientName) && (
            <Field label="Vinculado a" className="md:col-span-2">
              {() => (
                <div className="flex flex-wrap gap-2 text-[13px] text-ink-2">
                  {linkedLeadName && (
                    <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[3px]">
                      Lead: {linkedLeadName}
                    </span>
                  )}
                  {linkedClientName && (
                    <span className="inline-flex items-center rounded-[6px] border border-line px-2 py-[3px]">
                      Cliente: {linkedClientName}
                    </span>
                  )}
                </div>
              )}
            </Field>
          )}

          <Field label="Responsável" error={errors.responsible_user_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.responsible_user_id}
                defaultValue=""
                {...register('responsible_user_id', { setValueAs: emptyToNull })}
              >
                <option value="">Sem responsável</option>
                {responsibleUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Origem" error={errors.origin?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.origin}
                defaultValue=""
                {...register('origin', { setValueAs: emptyToNull })}
              >
                {ORIGIN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {/* Produto + valor */}
      <Card>
        <SectionTitle>Produto e valor</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Produto SaaS" error={errors.saas_product_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.saas_product_id}
                defaultValue=""
                {...register('saas_product_id', { setValueAs: emptyToNull })}
              >
                <option value="">—</option>
                {saasProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Plano" error={errors.plan_id?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.plan_id}
                defaultValue=""
                {...register('plan_id', { setValueAs: emptyToNull })}
              >
                <option value="">—</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Valor estimado (R$)" error={errors.value?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                tabular
                inputMode="decimal"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                hasError={!!errors.value}
                {...register('value', { setValueAs: valueToNumber })}
              />
            )}
          </Field>

          <Field
            label="Fechamento esperado"
            error={errors.expected_close_date?.message}
            hint="Datas passadas são permitidas (deal atrasado)"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="date"
                hasError={!!errors.expected_close_date}
                {...register('expected_close_date', { setValueAs: emptyToNull })}
              />
            )}
          </Field>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-[22px] flex items-center justify-end gap-2 border-t border-line bg-page/90 px-[22px] py-3 backdrop-blur">
        {onCancel && (
          <PillButton onClick={onCancel} disabled={isPending || isSubmitting}>
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending || isSubmitting || !isValid}>
          {isPending || isSubmitting ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
